import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ReadinessState } from '@/lib/readiness';
import type { DominantPowerStateResult } from '@/hooks/useDominantPowerState';
import { DominantPowerStateCard } from '@/components/dashboard/DominantPowerStateCard';
import { assessmentCopy } from '@/lib/inline-copy';

interface EnergyPositionProps {
  /** Total energy remaining in kWh */
  energyRemainingKwh: number;
  /** Usable energy (above reserve) in kWh */
  usableKwh: number;
  /** Reserve energy in kWh */
  reserveKwh: number;
  /** Total capacity in kWh */
  totalCapacityKwh: number;
  /** Time to sunrise (formatted string like "9h 20m") */
  timeToSunrise: string;
  /** Dominant power state for headline box (replaces Net Power) */
  dominantState: DominantPowerStateResult;
  /** Forecast data for assessment */
  forecastData?: {
    worstCase: number; // Worst case energy at sunrise in kWh
  };
  /** Backend-authoritative readiness; gates display and red/destructive states */
  readinessState?: ReadinessState;
  /** When not 'action_required', the assessment callout is hidden (chart and guidance tell the story). */
  systemRiskState?: 'ok' | 'watch' | 'action_required';
  /** Effective capacity from backend (optional); when differs from totalCapacityKwh, show "expected" note. */
  effectiveCapacityKwh?: number | null;
  /** Configured capacity (optional); for comparison with effective. */
  configuredCapacityKwh?: number;
}

type AssessmentState = 'healthy' | 'watch' | 'critical';

function getEnergyPositionState(
  usableKwh: number,
  reserveKwh: number,
  forecastData?: { worstCase: number }
): AssessmentState {
  const totalUsable = usableKwh + reserveKwh;
  const percentUsable = totalUsable > 0 ? (usableKwh / totalUsable) * 100 : 0;
  const worstCaseBreachesReserve = forecastData ? forecastData.worstCase < reserveKwh : false;

  if (percentUsable > 70 && !worstCaseBreachesReserve) {
    return 'healthy';
  }

  if (percentUsable < 30 || worstCaseBreachesReserve) {
    return 'critical';
  }

  return 'watch';
}

const assessmentIcons = {
  healthy: CheckCircle2,
  watch: AlertTriangle,
  critical: XCircle,
};

export function EnergyPosition({
  energyRemainingKwh,
  usableKwh,
  reserveKwh,
  totalCapacityKwh,
  timeToSunrise,
  dominantState,
  forecastData,
  readinessState = 'ready',
  systemRiskState,
  effectiveCapacityKwh,
  configuredCapacityKwh,
}: EnergyPositionProps) {
  const showCapacityComparison =
    effectiveCapacityKwh != null &&
    configuredCapacityKwh != null &&
    Math.abs(effectiveCapacityKwh - configuredCapacityKwh) > 0.5;
  const isConnecting = readinessState === 'connecting';
  const isMuted = readinessState === 'warming_up' || readinessState === 'learning';
  const isDegraded = readinessState === 'degraded';
  const showFullAssessment = readinessState === 'ready';
  const percentUsable = totalCapacityKwh > 0 ? (usableKwh / (totalCapacityKwh - reserveKwh)) * 100 : 0;
  const percentTotal = totalCapacityKwh > 0 ? (energyRemainingKwh / totalCapacityKwh) * 100 : 0;
  const rawAssessmentState = getEnergyPositionState(usableKwh, reserveKwh, forecastData);
  const assessmentState = showFullAssessment ? rawAssessmentState : 'healthy';
  const assessment = assessmentCopy(assessmentState, {
    energyRemainingKwh,
    usableKwh,
    reserveKwh,
    worstCaseKwh: forecastData?.worstCase,
    timeToSunrise,
  });
  const AssessmentIcon = assessmentIcons[assessmentState];

  const assessmentColor =
    assessmentState === 'healthy'
      ? 'text-wt-green-primary'
      : assessmentState === 'watch'
        ? 'text-wt-amber-primary'
        : 'text-destructive';
  const assessmentBg =
    assessmentState === 'healthy'
      ? 'bg-wt-green-primary/5 border-l-wt-green-primary'
      : assessmentState === 'watch'
        ? 'bg-wt-amber-primary/10 border-l-wt-amber-primary'
        : 'bg-destructive/10 border-l-destructive';

  const cardClass = [
    'rounded-lg border border-border bg-wt-bg-secondary p-4 w-full max-w-[360px]',
    isMuted && 'opacity-80',
    isDegraded && 'opacity-75',
  ].filter(Boolean).join(' ');

  const energyDisplay = isConnecting ? '—' : energyRemainingKwh.toFixed(1);
  const usableDisplay = isConnecting ? '—' : usableKwh.toFixed(1);
  const reserveDisplay = isConnecting ? '—' : reserveKwh.toFixed(1);
  const percentDisplay = isConnecting ? '—' : `${Math.round(percentTotal)}%`;
  const timeDisplay = isConnecting ? '—' : timeToSunrise;

  return (
    <div className={cardClass}>
      {/* Available Energy Section */}
      <div className="mb-5">
        <div className="mb-3">
          <div className="flex items-center gap-1.5 mb-3">
            <label className="label text-wt-text-secondary block">
              Available Energy
            </label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" aria-hidden />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Current usable battery energy based on charge level.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="metric-hero text-foreground mb-3">
            <span className="font-mono-numeric">{energyDisplay}</span> kWh
          </div>
        </div>

        {/* Progress Bar — hidden when connecting */}
        {!isConnecting && (
          <div className="relative mb-4">
            <div className="absolute right-0 top-[-28px] font-mono-numeric text-lg font-semibold text-wt-text-secondary">
              {percentDisplay}
            </div>
            <div className="w-full h-[6px] bg-wt-bg-tertiary rounded-[3px] overflow-hidden">
              <div
                className={`h-full rounded-[3px] transition-all duration-300 ${
                  assessmentState === 'healthy'
                    ? 'bg-wt-green-primary'
                    : assessmentState === 'watch'
                    ? 'bg-wt-amber-primary'
                    : 'bg-destructive'
                }`}
                style={{ width: `${percentTotal}%` }}
              />
            </div>
          </div>
        )}

        {/* Breakdown */}
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-wt-text-secondary">Usable:</span>
            <span className="font-mono-numeric text-sm font-medium text-wt-text-secondary">
              {usableDisplay} kWh
            </span>
          </div>
          <div className="flex justify-between items-center gap-1">
            <span className="text-sm text-wt-text-secondary flex items-center gap-1">
              Reserve threshold
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground cursor-help shrink-0" aria-hidden />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Minimum energy kept in battery; below this the system may shut down.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
            <span className="font-mono-numeric text-sm font-medium text-wt-text-secondary">
              {reserveDisplay} kWh
            </span>
          </div>
          <div className="flex justify-between items-center gap-1">
            <span className="text-sm text-wt-text-secondary flex items-center gap-1">
              Total Capacity
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground cursor-help shrink-0" aria-hidden />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Maximum energy your battery system can store.</p>
                    {showCapacityComparison && (
                      <p className="mt-1 text-sm text-wt-text-secondary">
                        Expected capacity from system specs/learning may differ slightly.
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
            <span className="font-mono-numeric text-sm font-medium text-wt-text-secondary">
              {isConnecting ? '—' : totalCapacityKwh.toFixed(1)} kWh
              {showCapacityComparison && effectiveCapacityKwh != null && (
                <span className="ml-1 text-sm text-wt-text-secondary font-normal">
                  (expected: {effectiveCapacityKwh.toFixed(1)})
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="h-px bg-border my-5" />

      {/* Time to Sunrise */}
      <div className="mb-5">
        <label className="label text-wt-text-secondary block mb-2">
          Time to Sunrise
        </label>
        <div className="metric-section text-foreground mb-1 font-mono-numeric">
          {timeDisplay}
        </div>
      </div>

      <div className="h-px bg-border my-5" />

      {/* Dominant power state (replaces Net Power) */}
      <DominantPowerStateCard
        state={dominantState}
        isConnecting={isConnecting}
        className="mb-5"
      />

      {/* Assessment callout: show for both watch and action_required states */}
      {(systemRiskState === 'action_required' || systemRiskState === 'watch') && (
        <div className={`p-4 rounded-lg border-l-[3px] mt-5 ${assessmentBg}`}>
          <div className="flex items-center gap-2 mb-2">
            <AssessmentIcon className={`w-4 h-4 ${assessmentColor}`} />
            <span className={`text-[13px] font-semibold uppercase tracking-[0.05em] ${assessmentColor}`}>
              {assessment.label}
            </span>
          </div>
          <p className="text-[13px] text-wt-text-secondary leading-[1.5]">
            {assessment.message}
          </p>
        </div>
      )}
    </div>
  );
}
